import { Button, Callout, Card, Dialog, H2, Icon, Intent, Spinner, Tag } from "@blueprintjs/core";
import maplibregl, { GeoJSONSource, Map as MapLibreMap, Marker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ILocationMatrix,
  ILocationMatrixPair,
  ILocationMatrixValue,
  ILocationValue,
  IPosition,
} from "components/Template/types";
import { applyFunction } from "components/Template/action";
import { doHidden } from "components/Template/utils";
import Validate from "components/Template/Validate";
import { IInputProps } from "../types";
import {
  calculateDrivingPair,
  hasAwsLocationConfiguration,
  mapStyleUrl,
} from "../Location/awsLocation";
import "./LocationMatrix.scss";

interface LocationMatrixProps extends IInputProps {
  template: ILocationMatrix;
}

const EMPTY_MATRIX: ILocationMatrixValue = {
  locationSignature: "",
  distanceMatrix: [],
  durationMatrix: [],
  pairs: [],
};
const METERS_PER_MILE = 1609.344;

const buildMatrix = (size: number, pairs: Array<ILocationMatrixPair>, field: "distanceMeters" | "durationSeconds") => {
  const matrix = Array.from({ length: size }, () => Array.from({ length: size }, () => 0));
  pairs.forEach((pair) => {
    matrix[pair.fromIndex][pair.toIndex] = pair[field];
    matrix[pair.toIndex][pair.fromIndex] = pair[field];
  });
  return matrix;
};

const formatDuration = (seconds: number) => {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hr${hours === 1 ? "" : "s"}${minutes % 60 ? ` ${minutes % 60} min` : ""}`;
};

interface RouteMapProps {
  active: boolean;
  className: string;
  testId: string;
  locations: Array<ILocationValue>;
  pairs: Array<ILocationMatrixPair>;
  locationSignature: string;
  displaySignature: string;
  center?: IPosition;
  zoom?: number;
  hoveredLocation?: number;
  hoveredPair?: string;
  onHoverLocation: (index?: number) => void;
  onHoverPair: (pair?: string) => void;
}

const RouteMap = ({
  active,
  className,
  testId,
  locations,
  pairs,
  locationSignature,
  displaySignature,
  center,
  zoom,
  hoveredLocation,
  hoveredPair,
  onHoverLocation,
  onHoverPair,
}: RouteMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap>();
  const markersRef = useRef<Array<Marker>>([]);
  const locationsRef = useRef(locations);
  locationsRef.current = locations;
  const pairsRef = useRef(pairs);
  pairsRef.current = pairs;
  const hoverPairRef = useRef(onHoverPair);
  hoverPairRef.current = onHoverPair;
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!active || !containerRef.current || mapRef.current || !mapStyleUrl) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: mapStyleUrl,
      center: center || locationsRef.current[0]?.position || [-122.3321, 47.6062],
      zoom: zoom ?? 9,
    });
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.on("load", () => {
      map.addSource("location-matrix-routes", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "location-matrix-route-outline",
        type: "line",
        source: "location-matrix-routes",
        paint: { "line-color": "#fff", "line-width": 9, "line-opacity": 0.9 },
      });
      map.addLayer({
        id: "location-matrix-route-line",
        type: "line",
        source: "location-matrix-routes",
        paint: { "line-color": "#137cbd", "line-width": 5, "line-opacity": 0.95 },
      });
      map.addLayer({
        id: "location-matrix-route-hit",
        type: "line",
        source: "location-matrix-routes",
        paint: { "line-color": "#000", "line-width": 16, "line-opacity": 0 },
      });
      map.on("mouseenter", "location-matrix-route-hit", (event) => {
        map.getCanvas().style.cursor = "pointer";
        const properties = event.features?.[0]?.properties;
        if (properties) hoverPairRef.current(`${properties.fromIndex}-${properties.toIndex}`);
      });
      map.on("mouseleave", "location-matrix-route-hit", () => {
        map.getCanvas().style.cursor = "";
        hoverPairRef.current(undefined);
      });
      setLoaded(true);
      map.resize();
    });
    mapRef.current = map;
    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = undefined;
      setLoaded(false);
    };
  }, [active, center, zoom]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const source = map.getSource("location-matrix-routes") as GeoJSONSource | undefined;
    source?.setData({
      type: "FeatureCollection",
      features: pairsRef.current.map((pair) => ({
        type: "Feature" as const,
        properties: { fromIndex: pair.fromIndex, toIndex: pair.toIndex },
        geometry: { type: "LineString" as const, coordinates: pair.geometry },
      })),
    });

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = locationsRef.current.map((location, index) => {
      const element = document.createElement("button");
      element.type = "button";
      element.className = "location-matrix__marker";
      element.textContent = String(index + 1);
      element.title = location.label;
      element.setAttribute("aria-label", `Location ${index + 1}: ${location.label}`);
      element.addEventListener("mouseenter", () => onHoverLocation(index));
      element.addEventListener("mouseleave", () => onHoverLocation(undefined));
      return new maplibregl.Marker({ element }).setLngLat(location.position as IPosition).addTo(map);
    });

    if (locationsRef.current.length) {
      const bounds = new maplibregl.LngLatBounds();
      locationsRef.current.forEach((location) => bounds.extend(location.position as IPosition));
      map.fitBounds(bounds, { padding: 55, maxZoom: 12, duration: 0 });
    }
  }, [loaded, locationSignature, displaySignature, onHoverLocation, pairs.length]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    let matching: any = true;
    if (hoveredPair) {
      const [fromIndex, toIndex] = hoveredPair.split("-").map(Number);
      matching = ["all", ["==", ["get", "fromIndex"], fromIndex], ["==", ["get", "toIndex"], toIndex]];
    } else if (hoveredLocation !== undefined) {
      matching = ["any", ["==", ["get", "fromIndex"], hoveredLocation], ["==", ["get", "toIndex"], hoveredLocation]];
    }
    const highlighting = Boolean(hoveredPair || hoveredLocation !== undefined);
    map.setPaintProperty("location-matrix-route-line", "line-opacity", highlighting ? ["case", matching, 1, 0.16] : 0.95);
    map.setPaintProperty("location-matrix-route-outline", "line-opacity", highlighting ? ["case", matching, 0.9, 0.08] : 0.9);
  }, [hoveredLocation, hoveredPair, loaded]);

  return (
    <div
      ref={containerRef}
      className={className}
      data-testid={testId}
      data-hovered-pair={hoveredPair || undefined}
    />
  );
};

const LocationMatrix = (props: LocationMatrixProps) => {
  const { template, mutators, model } = props;
  let locations: Array<ILocationValue> = [];
  let locationsError: string | undefined;
  try {
    const configured = Array.isArray(template.locations)
      ? template.locations
      : applyFunction(model, template.locations, mutators.getValue);
    if (Array.isArray(configured)) {
      locations = configured;
    } else {
      locationsError = "The locations expression must return an array.";
    }
  } catch (error: any) {
    locationsError = error?.message || "Unable to read the configured locations.";
  }
  const value = (mutators.getValue(template.name) as ILocationMatrixValue | undefined) || EMPTY_MATRIX;
  const valueRef = useRef(value);
  valueRef.current = value;
  const locationsRef = useRef(locations);
  locationsRef.current = locations;
  const handleChangeRef = useRef(mutators.handleChange);
  handleChangeRef.current = mutators.handleChange;
  const [routing, setRouting] = useState(false);
  const [error, setError] = useState<string>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogOpened, setDialogOpened] = useState(false);
  const [hoveredLocation, setHoveredLocation] = useState<number>();
  const [hoveredPair, setHoveredPair] = useState<string>();

  const locationSignature = locations
    .map((location, index) => `${index}:${location?.position?.join(",") || "unresolved"}`)
    .join("|");
  const displaySignature = JSON.stringify(locations.map((location) => location?.label || ""));
  const resolvedLocations = locations.filter((location): location is ILocationValue => Boolean(location?.position));
  const allResolved = resolvedLocations.length === locations.length;
  const expectedPairs = (locations.length * (locations.length - 1)) / 2;

  const updateValue = useCallback((next: ILocationMatrixValue) => {
    valueRef.current = next;
    handleChangeRef.current?.(template.name, next);
  }, [template.name]);

  useEffect(() => {
    const currentLocations = locationsRef.current;
    if (currentLocations.length < 2 || !allResolved) {
      setRouting(false);
      setError(undefined);
      if (valueRef.current.locationSignature !== locationSignature || valueRef.current.pairs.length) {
        updateValue({ ...EMPTY_MATRIX, locationSignature });
      }
      return;
    }
    if (valueRef.current.locationSignature === locationSignature && valueRef.current.pairs.length === expectedPairs) {
      return;
    }

    const controller = new AbortController();
    const requests: Array<Promise<ILocationMatrixPair>> = [];
    for (let fromIndex = 0; fromIndex < currentLocations.length; fromIndex += 1) {
      for (let toIndex = fromIndex + 1; toIndex < currentLocations.length; toIndex += 1) {
        requests.push(calculateDrivingPair(
          currentLocations[fromIndex] as ILocationValue,
          currentLocations[toIndex] as ILocationValue,
          fromIndex,
          toIndex,
          controller.signal
        ));
      }
    }

    setRouting(true);
    setError(undefined);
    updateValue({ ...EMPTY_MATRIX, locationSignature });
    Promise.all(requests)
      .then((pairs) => {
        if (controller.signal.aborted) return;
        updateValue({
          locationSignature,
          pairs,
          distanceMatrix: buildMatrix(currentLocations.length, pairs, "distanceMeters"),
          durationMatrix: buildMatrix(currentLocations.length, pairs, "durationSeconds"),
          calculatedAt: new Date().toISOString(),
        });
      })
      .catch((requestError: any) => {
        if (requestError?.name !== "AbortError") {
          setError(requestError?.message || "Unable to calculate the driving matrix.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setRouting(false);
      });
    return () => controller.abort();
  }, [allResolved, expectedPairs, locationSignature, updateValue]);

  if (doHidden(props)) return null;

  const ready = value.locationSignature === locationSignature && value.pairs.length === expectedPairs && expectedPairs > 0;
  return (
    <Validate {...props} className={`location-matrix anchor-${template.name}`} showLabel showInfo>
      <div className={`location-matrix anchor-${template.name}`}>
        <Card className="location-matrix__preview" elevation={0}>
          <div className="location-matrix__preview-main">
            <div className="location-matrix__preview-title"><Icon icon="path-search" /> Driving distance matrix</div>
            <div className="location-matrix__preview-meta" aria-live="polite">
              <span>{locations.length} location{locations.length === 1 ? "" : "s"}</span>
              <span>{expectedPairs} unique route{expectedPairs === 1 ? "" : "s"}</span>
              {routing && <span><Spinner size={14} /> Calculating…</span>}
              {ready && <Tag minimal intent={Intent.SUCCESS}>Up to date</Tag>}
            </div>
          </div>
          <Button icon="maximize" intent={Intent.PRIMARY} outlined onClick={() => setDialogOpen(true)} disabled={!locations.length}>
            Expand map
          </Button>
        </Card>
        <RouteMap
          active
          className="location-matrix__map location-matrix__map--inline"
          testId="location-matrix-map-inline"
          locations={resolvedLocations}
          pairs={value.pairs}
          locationSignature={locationSignature}
          displaySignature={displaySignature}
          center={template.center}
          zoom={template.zoom}
          hoveredLocation={hoveredLocation}
          hoveredPair={hoveredPair}
          onHoverLocation={setHoveredLocation}
          onHoverPair={setHoveredPair}
        />
        {locations.length < 2 && <Callout intent={Intent.PRIMARY}>Add at least two resolved locations to calculate the matrix.</Callout>}
        {!allResolved && locations.length >= 2 && <Callout intent={Intent.WARNING}>Choose an autocomplete result for every location.</Callout>}
        {!hasAwsLocationConfiguration && <Callout intent={Intent.DANGER}>Amazon Location is not configured.</Callout>}
        {locationsError && <Callout intent={Intent.DANGER}>{locationsError}</Callout>}
        {error && <Callout intent={Intent.DANGER}>{error}</Callout>}
      </div>

      <Dialog
        className="location-matrix-dialog"
        isOpen={dialogOpen}
        onOpened={() => setDialogOpened(true)}
        onClosed={() => setDialogOpened(false)}
        onClose={() => setDialogOpen(false)}
      >
        <div className="form__header">
          <H2>Driving Distance Matrix</H2>
          <Button minimal icon="cross" aria-label="Close" onClick={() => setDialogOpen(false)} />
        </div>
        <div className="location-matrix__workspace">
          <div className="location-matrix__sidebar">
            <ol aria-label="Mapped locations">
              {locations.map((location, index) => (
                <li
                  key={index}
                  className={hoveredLocation === index ? "location-matrix__location--hovered" : ""}
                  onMouseEnter={() => setHoveredLocation(index)}
                  onMouseLeave={() => setHoveredLocation(undefined)}
                >
                  <span>{index + 1}</span>
                  <span title={location?.label}>{location?.label || "Unresolved location"}</span>
                </li>
              ))}
            </ol>
            <p>Hover over a location or pin to emphasize every route connected to it.</p>
          </div>
          <RouteMap
            active={dialogOpened}
            className="location-matrix__map location-matrix__map--dialog"
            testId="location-matrix-map-dialog"
            locations={resolvedLocations}
            pairs={value.pairs}
            locationSignature={locationSignature}
            displaySignature={displaySignature}
            center={template.center}
            zoom={template.zoom}
            hoveredLocation={hoveredLocation}
            hoveredPair={hoveredPair}
            onHoverLocation={setHoveredLocation}
            onHoverPair={setHoveredPair}
          />
        </div>
        {routing && <div className="location-matrix__loading"><Spinner size={18} /> Calculating {expectedPairs} unique routes…</div>}
        {ready && (
          <div className="location-matrix__table-wrap">
            <table aria-label="Driving distance matrix in miles">
              <thead><tr><th></th>{locations.map((_, index) => <th key={index}>{index + 1}</th>)}</tr></thead>
              <tbody>
                {value.distanceMatrix.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <th>{rowIndex + 1}</th>
                    {row.map((meters, columnIndex) => (
                      <td key={columnIndex} title={rowIndex === columnIndex ? "Same location" : formatDuration(value.durationMatrix[rowIndex][columnIndex])}>
                        {(meters / METERS_PER_MILE).toFixed(1)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <span>miles</span>
          </div>
        )}
        {error && <Callout intent={Intent.DANGER}>{error}</Callout>}
        <div className="form__footer"><div></div><Button intent={Intent.PRIMARY} onClick={() => setDialogOpen(false)}>Done</Button></div>
      </Dialog>
    </Validate>
  );
};

export default LocationMatrix;
